import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, MessageSquare, Wrench, Settings, Package, Star, ArrowRight } from 'lucide-react';
import { apiService, Statistics } from '../services/api';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await apiService.getStatistics();
        setStats(data);
      } catch (error) {
        console.error('Failed to fetch statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  const statCards = [
    { title: 'Total Services', value: stats?.total_services || 0, icon: Settings, color: 'text-blue-600', bg: 'bg-blue-100' },
    { title: 'Total Products', value: stats?.total_products || 0, icon: Package, color: 'text-green-600', bg: 'bg-green-100' },
    { title: 'Blog Posts', value: stats?.total_blog_posts || 0, icon: Users, color: 'text-purple-600', bg: 'bg-purple-100' },
    { title: 'Pending Contacts', value: stats?.pending_requests || 0, icon: MessageSquare, color: 'text-yellow-600', bg: 'bg-yellow-100' },
    { title: 'Finished Services', value: stats?.completed_requests || 0, icon: Wrench, color: 'text-orange-600', bg: 'bg-orange-100' },
    { title: 'Testimonials', value: stats?.total_testimonials || 0, icon: Star, color: 'text-pink-600', bg: 'bg-pink-100' },
  ];

  const chartData = [
    { name: 'Services', value: stats?.total_services || 0 },
    { name: 'Products', value: stats?.total_products || 0 },
    { name: 'Blogs', value: stats?.total_blog_posts || 0 },
    { name: 'Reviews', value: stats?.total_testimonials || 0 },
  ];

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Overview of your BD Electric administration</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
        {statCards.map((card, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className={`${card.bg} ${card.color} w-10 h-10 rounded-lg flex items-center justify-center mb-4`}>
              <card.icon className="h-5 w-5" />
            </div>
            <p className="text-sm font-medium text-gray-500 mb-1">{card.title}</p>
            <p className="text-2xl font-bold text-gray-900">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 min-w-0">
          <h2 className="text-lg font-bold text-gray-900 mb-6 font-display">System Distribution</h2>
          <div className="h-[300px] w-full min-h-0 min-w-0">
            {hasMounted && (
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9ca3af' }} />
                  <YAxis hide />
                  <Tooltip cursor={{ fill: '#f9fafb' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                  <Bar dataKey="value" fill="#ea580c" radius={[4, 4, 0, 0]} barSize={40} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
          <h2 className="text-lg font-bold text-gray-900 mb-6">Quick Shortcuts</h2>
          <div className="space-y-4 flex-1">
            <button onClick={() => navigate('/contact-requests')} className="w-full flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition drop-shadow-sm">
              <div className="flex items-center">
                <MessageSquare className="h-5 w-5 text-orange-600 mr-3" />
                <span className="font-medium text-gray-700">Handle Inquiries</span>
              </div>
              <div className="flex items-center text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-full font-bold">
                {stats?.pending_requests} New
              </div>
            </button>
            <button onClick={() => navigate('/services')} className="w-full flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition drop-shadow-sm">
              <div className="flex items-center">
                <Settings className="h-5 w-5 text-orange-600 mr-3" />
                <span className="font-medium text-gray-700">Update Services</span>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400" />
            </button>
            <button onClick={() => navigate('/service-requests')} className="w-full flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition drop-shadow-sm">
              <div className="flex items-center">
                <Wrench className="h-5 w-5 text-orange-600 mr-3" />
                <span className="font-medium text-gray-700">Manage Bookings</span>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;